/**
 * Feature-flag helpers for integrations.
 *
 * Connector entitlements and skill gates use the full connector RID (lower-cased),
 * e.g. `connector.otto-schmidt.legal-data-hub`.
 * Firm unlocks use `{rid}_is_bookable`, e.g. `connector.actaport.de_is_bookable`.
 */

export function connectorFeatureFlag(connectorRid: string | undefined): string | undefined {
  const normalized = connectorRid?.trim().toLowerCase();
  return normalized || undefined;
}

/** Connector rids live under the `connector.` namespace, which marks a flag as an integration flag. */
export function isConnectorFeatureFlag(flag: string): boolean {
  return flag.toLowerCase().startsWith('connector.');
}

/** Firm unlock flag: ``connector.advoware.de_is_bookable``. */
export function connectorIsBookableFeatureFlag(
  connectorRid: string | undefined,
): string | undefined {
  const flag = connectorFeatureFlag(connectorRid);
  if (!flag) {
    return undefined;
  }
  return `${flag}_is_bookable`;
}

/** True when the law firm has any ``*_is_bookable`` integration unlock. */
export function lawFirmHasIntegrationAccess(enabledFeatures: readonly string[] | undefined): boolean {
  return (enabledFeatures ?? []).some((flag) => flag.toLowerCase().endsWith('_is_bookable'));
}

/** True when the feature is enabled in the list of features. */
export function hasFeature(
  features: readonly string[] | undefined,
  required: string | undefined,
): boolean {
  if (!required) {
    return true;
  }
  // Both sides are normalized (as `sanitize_feature_flags` does): Stripe add-on metadata written
  // as a JSON array reaches `enabled_features` with its original casing.
  return (features ?? []).some((feature) => feature.toLowerCase() === required.toLowerCase());
}

/** True when features include the full connector RID. */
export function hasConnectorFeature(
  features: readonly string[] | undefined,
  connectorRid: string | undefined,
): boolean {
  return hasFeature(features, connectorFeatureFlag(connectorRid));
}

/** True when firm features include ``{rid}_is_bookable`` for this connector. */
export function hasConnectorIsBookable(
  features: readonly string[] | undefined,
  connectorRid: string | undefined,
): boolean {
  const bookable = connectorIsBookableFeatureFlag(connectorRid);
  if (!bookable) {
    return true;
  }
  return (features ?? []).some((feature) => feature.toLowerCase() === bookable);
}
